import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { ALL_SEASONS, type StatEntry } from '@gl/shared';

import { useStats } from '../api/hooks.js';
import { ErrorState, Loading } from '../components/states.js';
import { SeasonSelector } from '../components/SeasonSelector.js';
import { StatTable } from '../components/StatTable.js';
import { StatsFreshness } from '../components/StatsFreshness.js';
import { formatDate } from '../util/format.js';

const seasonLabel = (season: string) => (season === ALL_SEASONS ? 'All Seasons' : season);

/** Groups statistics into their categories, keeping the server's ordering. */
function groupByCategory(stats: StatEntry[]): Array<{ label: string; entries: StatEntry[] }> {
  const groups = new Map<string, { label: string; entries: StatEntry[] }>();

  for (const stat of stats) {
    const group = groups.get(stat.category) ?? { label: stat.categoryLabel, entries: [] };
    group.entries.push(stat);
    groups.set(stat.category, group);
  }

  return [...groups.values()];
}

function StatCard({
  stat,
  season,
  targeted,
  ref,
}: {
  stat: StatEntry;
  season: string;
  targeted: boolean;
  ref: (node: HTMLElement | null) => void;
}) {
  return (
    <article
      ref={ref}
      id={`stat-${stat.key}`}
      // The scroll margin clears the sticky header and the season selector that
      // floats under it, which would otherwise sit over the title of a
      // statistic someone followed a link to.
      className={`scroll-mt-40 border bg-field-900 ${
        targeted ? 'border-brass-400' : 'border-field-800'
      }`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-field-800 bg-field-950 px-4 py-2.5">
        <h3 className="font-display text-2xl font-bold uppercase leading-none tracking-tight text-field-50">
          {stat.title}
        </h3>
        {stat.computedAt ? (
          <span className="font-display text-xs font-semibold uppercase tracking-bug text-field-600">
            Updated {formatDate(stat.computedAt)}
          </span>
        ) : null}
      </div>

      <div className="p-4 sm:p-5">
        <p className="mb-4 max-w-3xl text-sm text-field-400">{stat.description}</p>
        {stat.table ? (
          <StatTable table={stat.table} title={stat.title} season={seasonLabel(season)} />
        ) : (
          <p className="border border-dashed border-field-700 px-4 py-6 text-center text-sm text-field-500">
            {stat.implemented
              ? 'Waiting on the next statistics run.'
              : 'This statistic has not been written yet.'}
          </p>
        )}
      </div>
    </article>
  );
}

export function StatsPage() {
  const [params, setParams] = useSearchParams();
  const requestedSeason = params.get('season') ?? '';
  const targetKey = params.get('stat');

  const { data, isPending, error } = useStats(requestedSeason);

  // The server resolves an unknown or missing season to the latest one, so its
  // answer — not the query string — decides which tab is lit.
  const season = data?.season ?? requestedSeason;
  const tabs = useMemo(() => [...(data?.seasons ?? []), ALL_SEASONS], [data?.seasons]);
  const groups = useMemo(() => groupByCategory(data?.stats ?? []), [data?.stats]);

  // The statistics in the order they are laid out down the page, which is the
  // order up and down walk them in.
  const order = useMemo(() => groups.flatMap((group) => group.entries.map((s) => s.key)), [groups]);

  const cards = useRef(new Map<string, HTMLElement>());

  const scrollToStat = useCallback((key: string) => {
    cards.current.get(key)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const select = useCallback(
    (next: { season?: string; stat?: string | null }) => {
      const updated = new URLSearchParams(params);
      if (next.season !== undefined) updated.set('season', next.season);
      if (next.stat !== undefined) {
        if (next.stat === null) updated.delete('stat');
        else updated.set('stat', next.stat);
      }
      setParams(updated, { replace: true });
      if (next.stat) scrollToStat(next.stat);
    },
    [params, setParams, scrollToStat],
  );

  /**
   * Up and down move which statistic is being looked at, rather than being a
   * second way to change the season: the page is a stack of tables and stepping
   * down it is what a reader wants those keys for. The first press with nothing
   * selected picks up whichever end they were heading towards.
   */
  const stepStat = useCallback(
    (delta: -1 | 1) => {
      if (order.length === 0) return;
      const current = targetKey ? order.indexOf(targetKey) : -1;
      if (current === -1) {
        select({ stat: (delta === 1 ? order[0] : order[order.length - 1]) as string });
        return;
      }
      const next = order[current + delta];
      if (next) select({ stat: next });
    },
    [order, targetKey, select],
  );

  // Deep links from the home page's statistic of the week land mid-page, so the
  // linked table has to come to the reader once its data has rendered.
  useEffect(() => {
    if (!targetKey || !data) return;
    scrollToStat(targetKey);
  }, [targetKey, data, scrollToStat]);

  return (
    <div>
      <div className="page-head flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
        <div>
          <p className="eyebrow mb-1">The numbers</p>
          <h1 className="page-title">Stats</h1>
          <p className="mt-2 text-sm text-field-400">
            {season === ALL_SEASONS
              ? 'Every season the league has played, combined.'
              : `The ${seasonLabel(season)} regular season.`}
          </p>
        </div>
        <div className="pt-1 sm:text-right">
          <StatsFreshness />
        </div>
      </div>

      {isPending ? <Loading /> : null}
      {error ? <ErrorState error={error} /> : null}

      {data ? (
        <>
          <SeasonSelector
            seasons={tabs}
            active={season}
            label={seasonLabel}
            onSelect={(next) => select({ season: next })}
            onVertical={stepStat}
            hint="Left and right change the season · up and down move through the statistics"
          />

          {data.stats.length === 0 ? (
            <p className="border border-dashed border-field-700 px-5 py-8 text-center text-sm text-field-500">
              No statistics apply to this season yet.
            </p>
          ) : (
            <>
              <nav aria-label="Statistics" className="card mb-10">
                <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {groups.map((group) => (
                    <div key={group.label}>
                      <h2 className="rail-title">{group.label}</h2>
                      <ul className="space-y-1">
                        {group.entries.map((stat) => (
                          <li key={stat.key}>
                            <button
                              type="button"
                              onClick={() => select({ stat: stat.key })}
                              className={`text-left text-sm transition-colors hover:text-brass-300 ${
                                stat.key === targetKey
                                  ? 'text-brass-300'
                                  : stat.table
                                    ? 'text-field-300'
                                    : 'text-field-500'
                              }`}
                            >
                              {stat.title}
                              {stat.table ? null : ' ·'}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-xs text-field-500">
                  A · marks a statistic that has not been calculated yet.
                </p>
              </nav>

              <div className="space-y-12">
                {groups.map((group) => (
                  <section key={group.label}>
                    <h2 className="section-title mb-3">{group.label}</h2>
                    <div className="space-y-5">
                      {group.entries.map((stat) => (
                        <StatCard
                          key={stat.key}
                          stat={stat}
                          season={season}
                          targeted={stat.key === targetKey}
                          ref={(node) => {
                            if (node) cards.current.set(stat.key, node);
                            else cards.current.delete(stat.key);
                          }}
                        />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            </>
          )}
        </>
      ) : null}
    </div>
  );
}
